'use client';

import styles from './ArticlePublisher.module.scss';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { Button, Checkbox, Group, Input, Select, Text, Textarea } from '@mantine/core';
import { Editor } from '@/src/components/EditorText/Editor';
import type { ChartType, RichBlock } from '@/src/types/RichBlock';

export function RichBlockEditor({
  block,
  onChange,
}: {
  block: RichBlock;
  onChange: (patch: Partial<RichBlock>) => void;
}) {
  if (block.type === 'chart') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre du graphique"
          mb="xs"
        />
        <Textarea
          value={(block.labels || []).join('\n')}
          onChange={(e) =>
            onChange({
              labels: e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="Labels (un par ligne)"
          minRows={3}
          mb="xs"
        />
        <Textarea
          value={(block.values || []).join('\n')}
          onChange={(e) =>
            onChange({
              values: e.target.value
                .split('\n')
                .map((s) => Number(s.trim()))
                .filter((n) => !Number.isNaN(n)),
            })
          }
          placeholder="Valeurs (une par ligne, nombres uniquement)"
          minRows={3}
          mb="xs"
        />
        <Select
          value={block.chartType}
          onChange={(value) => onChange({ chartType: value as ChartType })}
          data={[
            { value: 'bar', label: 'Barres' },
            { value: 'line', label: 'Ligne' },
            { value: 'pie', label: 'Camembert' },
          ]}
          mb="xs"
        />
        <Textarea
          value={block.caption ?? ''}
          onChange={(e) => onChange({ caption: e.target.value })}
          placeholder="Légende"
          minRows={2}
        />
      </div>
    );
  }

  if (block.type === 'checklist') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre de la checklist"
          mb="xs"
        />
        {block.items.map((item, idx) => (
          <Group key={idx} mb="xs" align="flex-start">
            <Input
              value={item.text}
              onChange={(e) => {
                const items = [...block.items];
                items[idx].text = e.target.value;
                onChange({ items });
              }}
              placeholder="Élément"
              style={{ flex: 1 }}
            />
            <Checkbox
              checked={item.checked ?? false}
              onChange={(e) => {
                const items = [...block.items];
                items[idx].checked = e.currentTarget.checked;
                onChange({ items });
              }}
              label="Coché"
            />
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => {
                const items = block.items.filter((_, i) => i !== idx);
                onChange({ items });
              }}
            >
              <IconTrash size={16} />
            </Button>
          </Group>
        ))}
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange({ items: [...block.items, { text: '', checked: false }] })}
        >
          Ajouter un élément
        </Button>
      </div>
    );
  }

  if (block.type === 'comparison') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre du tableau"
          mb="xs"
        />
        <Textarea
          value={(block.headers || []).join('\n')}
          onChange={(e) =>
            onChange({
              headers: e.target.value
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="En-têtes (un par ligne)"
          minRows={2}
          mb="xs"
        />
        <Text size="sm" fw={600} mb="xs">
          Lignes
        </Text>
        {block.rows.map((row, ri) => (
          <Group key={ri} mb="xs" align="flex-start">
            {row.map((cell, ci) => (
              <Input
                key={ci}
                value={cell}
                onChange={(e) => {
                  const rows = block.rows.map((r) => [...r]);
                  rows[ri][ci] = e.target.value;
                  onChange({ rows });
                }}
                placeholder={`Colonne ${ci + 1}`}
                style={{ flex: 1 }}
              />
            ))}
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => {
                const rows = block.rows.filter((_, i) => i !== ri);
                onChange({ rows });
              }}
            >
              <IconTrash size={16} />
            </Button>
          </Group>
        ))}
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => {
            const width = block.headers.length || 2;
            onChange({ rows: [...block.rows, Array(width).fill('')] });
          }}
          mb="xs"
        >
          Ajouter une ligne
        </Button>
        <Textarea
          value={block.caption ?? ''}
          onChange={(e) => onChange({ caption: e.target.value })}
          placeholder="Légende"
          minRows={2}
        />
      </div>
    );
  }

  if (block.type === 'expandable') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre de l’accordéon"
          mb="xs"
        />
        <Textarea
          value={block.content}
          onChange={(e) => onChange({ content: e.target.value })}
          placeholder="Contenu"
          minRows={3}
        />
      </div>
    );
  }

  if (block.type === 'richText') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre de la zone (optionnel)"
          mb="xs"
        />
        <Input
          type="color"
          value={block.backgroundColor ?? '#ffffff'}
          onChange={(e) => onChange({ backgroundColor: e.target.value })}
          placeholder="Couleur de fond"
          mb="xs"
        />
        <Group mb="xs" align="flex-start">
          <Input
            value={block.linkUrl ?? ''}
            onChange={(e) => onChange({ linkUrl: e.target.value })}
            placeholder="URL du lien interne"
            style={{ flex: 1 }}
          />
          <Input
            value={block.linkLabel ?? ''}
            onChange={(e) => onChange({ linkLabel: e.target.value })}
            placeholder="Texte du lien"
            style={{ flex: 1 }}
          />
        </Group>
        <Editor
          content={block.content ?? '<p></p>'}
          setEditorContent={(html) => onChange({ content: html })}
        />
      </div>
    );
  }

  if (block.type === 'imagePlaceholder') {
    return (
      <div className={styles.richBlockForm}>
        <Textarea
          value={block.prompt}
          onChange={(e) => onChange({ prompt: e.target.value })}
          placeholder="Prompt de l’image (en anglais, pour un générateur d’images)"
          minRows={3}
          mb="xs"
        />
        <Textarea
          value={block.caption ?? ''}
          onChange={(e) => onChange({ caption: e.target.value })}
          placeholder="Légende"
          minRows={2}
        />
      </div>
    );
  }

  if (block.type === 'quiz') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre du quiz"
          mb="xs"
        />
        {block.questions.map((q, qi) => (
          <div key={qi} className={styles.quizQuestionCard}>
            <Group justify="space-between" mb="xs">
              <Text size="sm" fw={600}>
                Question {qi + 1}
              </Text>
              <Button
                variant="subtle"
                color="red"
                size="xs"
                onClick={() => {
                  const questions = block.questions.filter((_, i) => i !== qi);
                  onChange({ questions });
                }}
              >
                <IconTrash size={16} />
              </Button>
            </Group>
            <Input
              value={q.question}
              onChange={(e) => {
                const questions = [...block.questions];
                questions[qi].question = e.target.value;
                onChange({ questions });
              }}
              placeholder="Question"
              mb="xs"
            />
            {q.choices.map((c, ci) => (
              <Group key={ci} mb="xs" align="center">
                <Input
                  value={c}
                  onChange={(e) => {
                    const questions = [...block.questions];
                    questions[qi].choices[ci] = e.target.value;
                    onChange({ questions });
                  }}
                  placeholder={`Choix ${ci + 1}`}
                  style={{ flex: 1 }}
                />
                <Checkbox
                  checked={q.correctIndex === ci}
                  onChange={() => {
                    const questions = [...block.questions];
                    questions[qi].correctIndex = ci;
                    onChange({ questions });
                  }}
                  label="Bonne"
                />
                <Button
                  variant="subtle"
                  color="red"
                  size="xs"
                  onClick={() => {
                    const questions = [...block.questions];
                    questions[qi].choices = q.choices.filter((_, i) => i !== ci);
                    if (questions[qi].correctIndex === ci) {
                      questions[qi].correctIndex = 0;
                    } else if (questions[qi].correctIndex > ci) {
                      questions[qi].correctIndex -= 1;
                    }
                    onChange({ questions });
                  }}
                >
                  <IconTrash size={16} />
                </Button>
              </Group>
            ))}
            <Button
              variant="light"
              size="xs"
              leftSection={<IconPlus size={14} />}
              onClick={() => {
                const questions = [...block.questions];
                questions[qi].choices.push('');
                onChange({ questions });
              }}
              mb="xs"
            >
              Ajouter un choix
            </Button>
            <Textarea
              value={q.explanation}
              onChange={(e) => {
                const questions = [...block.questions];
                questions[qi].explanation = e.target.value;
                onChange({ questions });
              }}
              placeholder="Explication de la bonne réponse"
              minRows={2}
            />
          </div>
        ))}
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            onChange({
              questions: [
                ...block.questions,
                { question: '', choices: ['', ''], correctIndex: 0, explanation: '' },
              ],
            })
          }
        >
          Ajouter une question
        </Button>
      </div>
    );
  }

  if (block.type === 'certificate') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre du certificat"
          mb="xs"
        />
        <Textarea
          value={block.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Description"
          minRows={2}
          mb="xs"
        />
        <Textarea
          value={block.criteria}
          onChange={(e) => onChange({ criteria: e.target.value })}
          placeholder="Critère d’obtention"
          minRows={2}
        />
      </div>
    );
  }

  if (block.type === 'confluenceTemplate') {
    return (
      <div className={styles.richBlockForm}>
        <Input
          value={block.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Titre du template"
          mb="xs"
        />
        <Textarea
          value={block.description}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Description / contexte d’usage"
          minRows={2}
          mb="xs"
        />
        <Input
          value={block.fileName ?? ''}
          onChange={(e) => onChange({ fileName: e.target.value })}
          placeholder="Nom du fichier (sans extension)"
          mb="xs"
        />
        {block.items.map((item, idx) => (
          <Group key={idx} mb="xs" align="flex-start">
            <Input
              value={item.text}
              onChange={(e) => {
                const items = [...block.items];
                items[idx].text = e.target.value;
                onChange({ items });
              }}
              placeholder="Élément de checklist"
              style={{ flex: 1 }}
            />
            <Checkbox
              checked={item.checked ?? false}
              onChange={(e) => {
                const items = [...block.items];
                items[idx].checked = e.currentTarget.checked;
                onChange({ items });
              }}
              label="Coché"
            />
            <Button
              variant="subtle"
              color="red"
              size="xs"
              onClick={() => {
                const items = block.items.filter((_, i) => i !== idx);
                onChange({ items });
              }}
            >
              <IconTrash size={16} />
            </Button>
          </Group>
        ))}
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange({ items: [...block.items, { text: '', checked: false }] })}
        >
          Ajouter un élément
        </Button>
      </div>
    );
  }

  return (
    <Text size="sm" c="dimmed">
      Édition non disponible pour ce type de bloc.
    </Text>
  );
}
